import { Alerter, Button, Callout, Dialog, FormGroup, Input, Intent } from '@pinpointhq/thumbtack';
import * as React from 'react';
import { Candidate } from '../../../../javascript/models';
import { createToast } from '../../../FlashToaster';

// Merges a duplicate candidate into this one: their applications move across and the duplicate is removed
export function MergeDialog({
  candidate,
  fetchData,
  handleClose,
}: {
  candidate: Candidate;
  fetchData(): void;
  handleClose(): void;
}) {
  const [search, setSearch] = React.useState('');
  const [matches, setMatches] = React.useState<Candidate[]>([]);

  const handleSearch = () => {
    if (!search.trim()) return;
    Candidate.where({ search: search.trim() })
      .per(10)
      .all()
      .then(({ data }) => setMatches(data.filter((match) => match.id !== candidate.id)));
  };

  const handleMerge = (duplicate: Candidate) => {
    Alerter.create({
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Merge',
      intent: Intent.DANGER,
      onConfirm: () => {
        candidate.mergingCandidateId = duplicate.id;
        candidate.save().then((success) => {
          if (success) {
            createToast({ text: `${duplicate.fullName} merged into ${candidate.fullName}`, type: 'success' });
            fetchData();
            handleClose();
          } else {
            candidate.mergingCandidateId = null;
            const error = candidate.errors.base;
            createToast({ text: error ? error.message : 'Unable to merge', type: 'error' });
          }
        });
      },
      text: `Move ${duplicate.fullName} (${duplicate.email})'s applications to ${candidate.fullName} and remove ${duplicate.fullName}? This can't be undone.`,
    });
  };

  return (
    <Dialog title={`Merge a duplicate into ${candidate.fullName}`} onClose={handleClose} isOpen={true}>
      <Callout intent={Intent.WARNING} className="mar-b-2">
        The duplicate's applications move to {candidate.fullName} ({candidate.email}) and the duplicate is removed. If
        both have an application in the same cycle, delete one of them first from the candidate's Applications tab.
      </Callout>
      <FormGroup label="Find the duplicate by name, email or ID">
        <Input
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
        />
      </FormGroup>
      <Button text="Search" onClick={handleSearch} className="mar-b-2" />
      {matches.map((match) => (
        <div key={match.id} className="mar-b-1">
          {match.fullName} ({match.email}){' '}
          <Button small={true} text="Merge into this candidate" onClick={() => handleMerge(match)} />
        </div>
      ))}
    </Dialog>
  );
}
